import axios from 'axios';
import { isPublicPath } from '@/utils/publicPaths';

// Use environment variable for API base URL, fallback to localhost for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

// Set base URL for all axios requests
axios.defaults.baseURL = API_BASE_URL;

// Add request interceptor to attach JWT token and tenant ID to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nexedu_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add X-Tenant-ID header from stored user data
    const userStr = localStorage.getItem('nexedu_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user?.tenantId) {
          config.headers['X-Tenant-ID'] = user.tenantId;
        }
      } catch {
        // Ignore JSON parse errors
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Single-flight token refresh shared across concurrent 401s.
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('nexedu_refresh_token');
  if (!refreshToken) return false;
  try {
    // Use a bare fetch (not this axios instance) so a failing refresh can't
    // recurse through this interceptor.
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    localStorage.setItem('nexedu_access_token', data.accessToken);
    localStorage.setItem('nexedu_refresh_token', data.refreshToken);
    // The new token may carry a changed role: let the auth layer re-sync the
    // cached user (ProtectedRoute listens). An event avoids importing the
    // store here, which would create an import cycle.
    window.dispatchEvent(new CustomEvent('nexedu:token-refreshed'));
    return true;
  } catch {
    return false;
  }
}

function clearAuthAndRedirect(): void {
  localStorage.removeItem('nexedu_access_token');
  localStorage.removeItem('nexedu_refresh_token');
  localStorage.removeItem('nexedu_user');
  localStorage.removeItem('nexedu-auth-storage');

  // Don't bounce users off public pages on 401. A stale token in
  // localStorage would otherwise hijack the password-reset flow or
  // dump anonymous visitors of the marketing site back on /login.
  if (!isPublicPath(window.location.pathname)) {
    window.location.href = '/login';
  }
}

// Add response interceptor to handle 401 and 403 errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // On a 401, transparently refresh the access token once and replay the
    // request, so a 15-minute token expiry self-heals instead of dumping the
    // user on /login (or showing a raw "jwt expired" error).
    if (error.response?.status === 401 && error.config && !error.config._retry) {
      error.config._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken();
      }
      const refreshed = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;

      if (refreshed) {
        error.config.headers = error.config.headers || {};
        error.config.headers.Authorization = `Bearer ${localStorage.getItem('nexedu_access_token')}`;
        return axios(error.config); // replay with the fresh token
      }
      // Refresh failed → session really is over.
      clearAuthAndRedirect();
    } else if (error.response?.status === 401) {
      clearAuthAndRedirect();
    }

    // Handle 403 Financial Suspension
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.message || '';

      // Check if this is a financial suspension error
      if (errorMessage.includes('suspendu pour raison financ') ||
          errorMessage.includes('suspendu pour raison financière')) {
        // Save suspension state to localStorage
        localStorage.setItem('nexedu_financial_suspended', 'true');
        localStorage.setItem('nexedu_financial_suspension_message', errorMessage);

        // Redirect to financial suspended page (unless already there)
        if (window.location.pathname !== '/financial-suspended') {
          window.location.href = '/financial-suspended';
        }
      }
    }

    // --- Make every error user-friendly (centralized) ---
    // Backend messages are usually clean French. But some are technical
    // (e.g. "ThrottlerException: Too Many Requests", "Internal Server Error")
    // and must never reach the user. We rewrite those into plain French and
    // normalise error.message so any consumer (login form, toasts…) shows it.
    const status: number | undefined = error.response?.status;
    let raw = error.response?.data?.message;
    if (Array.isArray(raw)) raw = raw.filter(Boolean).join(' ');

    const BY_STATUS: Record<number, string> = {
      429: "Trop de tentatives. Patientez un court instant, puis réessayez.",
      500: "Une erreur inattendue est survenue de notre côté. Réessayez dans un instant.",
      502: "Service momentanément indisponible. Réessayez dans un instant.",
      503: "Service momentanément indisponible. Réessayez dans un instant.",
      504: "Le serveur a mis trop de temps à répondre. Réessayez.",
    };

    const isTechnical =
      typeof raw !== 'string' ||
      raw.trim() === '' ||
      /exception|too many requests|internal server error|econnrefused|cannot (get|post|put|patch|delete)|jwt (expired|malformed)|token expired|unauthorized|<html/i.test(raw);

    let friendly: string;
    if (!error.response) {
      friendly = "Impossible de contacter le serveur. Vérifiez votre connexion Internet.";
    } else if (status && BY_STATUS[status]) {
      friendly = BY_STATUS[status];
    } else if (isTechnical) {
      friendly = "Une erreur est survenue. Veuillez réessayer.";
    } else {
      friendly = raw as string; // backend already returned a clean, human message
    }

    // Expose the friendly text on both fields so every caller renders it.
    error.message = friendly;
    if (error.response?.data && (!error.response.data.message || (status && BY_STATUS[status]) || isTechnical)) {
      error.response.data.message = friendly;
    }

    return Promise.reject(error);
  }
);

export default axios;
